import { useScenarioForm } from '../contexts';
import {
  DropdownField,
  NumberField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { Model, ModelParameter } from 'types/dca-scenarios-api/responses';
import { startCase } from 'lodash-es';
import styles from './common-fields.module.scss';
import commonStyles from '../common.module.scss';
import { ProductionStreamConfigData, StreamConfigKey } from '../types';
import { useMemo } from 'react';
import { Checkbox } from '@carbon/react';
import { getMethodParameterMapping, getValueByPath } from '../utils';
import { getDefaultParameterValuesByType } from '../default-values';

const modelOptionsMapping = {
  non_ramp_up: ['modified_hyperbolic', 'hyperbolic', 'exponential', 'ratio'],
  ramp_up: ['linear', 'constant', 'exponential'],
};

const RESIDUAL_FUNCTION_OPTIONS = ['log', 'linear'] as const;

const LOSS_FUNCTION_OPTIONS = [
  'least_squares',
  'soft_l1',
  'huber',
  'cauchy',
  'arctan',
] as const;

const QI_BOUND_TYPE_OPTIONS = ['absolute', 'relative'] as const;

interface CommonFieldsProps {
  streamKey: StreamConfigKey;
  configIndex: number;
  config: ProductionStreamConfigData;
}

export const CommonFields = ({
  streamKey,
  configIndex,
  config,
}: CommonFieldsProps) => {
  const { form, baselines } = useScenarioForm();

  const { basePath, baseId, modelType } = useMemo(() => {
    return {
      basePath: `${streamKey}[${configIndex}]` as const,
      baseId: `${config.stream}-${configIndex}`,
      modelType: config.modelType,
    };
  }, [streamKey, config, configIndex]);

  const handleModelChange = (value: Model) => {
    const paramKeys = getMethodParameterMapping(modelType)[value];

    if (
      config.modelType === 'biased' &&
      config.lossFunction === 'mlle' &&
      config.constrainEur
    ) {
      paramKeys.push('eurMbbl');
    }

    const existingInitialGuess = getValueByPath(
      baselines as unknown as Record<string, unknown>,
      `${basePath}.initialGuess`
    ) as Record<ModelParameter, number>;

    const existingLowerBounds = getValueByPath(
      baselines as unknown as Record<string, unknown>,
      `${basePath}.lowerBounds`
    ) as Record<ModelParameter, number>;
    const existingUpperBounds = getValueByPath(
      baselines as unknown as Record<string, unknown>,
      `${basePath}.upperBounds`
    ) as Record<ModelParameter, number>;
    const existingDistributions = getValueByPath(
      baselines as unknown as Record<string, unknown>,
      `${basePath}.distributions`
    ) as Record<ModelParameter | 'eurMbbl', string>;
    const existingLockedParameters = getValueByPath(
      baselines as unknown as Record<string, unknown>,
      `${basePath}.lockedParameters`
    ) as Record<ModelParameter | 'eurMbbl', number | null>;

    const defaultInitialGuess = getDefaultParameterValuesByType(
      value,
      modelType,
      'initialGuess'
    );

    const defaultLowerBounds = getDefaultParameterValuesByType(
      value,
      modelType,
      'lowerBounds'
    );
    const defaultUpperBounds = getDefaultParameterValuesByType(
      value,
      modelType,
      'upperBounds'
    );

    const initialGuess = {} as Record<ModelParameter, number>;
    const lowerBounds = {} as Record<ModelParameter, number>;
    const upperBounds = {} as Record<ModelParameter, number>;
    const distributions = {} as Record<ModelParameter | 'eurMbbl', string>;
    const lockedParameters = {} as Record<ModelParameter, number | null>;

    paramKeys.forEach((key) => {
      initialGuess[key as ModelParameter] =
        existingInitialGuess?.[key as ModelParameter] ||
        (key === 'eurMbbl'
          ? 1000
          : (defaultInitialGuess[key as ModelParameter] as number));
      lowerBounds[key as ModelParameter] =
        existingLowerBounds?.[key as ModelParameter] ||
        (key === 'eurMbbl'
          ? 1000
          : (defaultLowerBounds[key as ModelParameter] as number));
      upperBounds[key as ModelParameter] =
        existingUpperBounds?.[key as ModelParameter] ||
        (key === 'eurMbbl'
          ? 1000
          : (defaultUpperBounds[key as ModelParameter] as number));

      if (
        key !== 'eurMbbl' &&
        !!existingLockedParameters?.[key as ModelParameter]
      ) {
        lockedParameters[key as ModelParameter] =
          existingLockedParameters[key as ModelParameter];
      }

      distributions[key as ModelParameter] =
        existingDistributions?.[key as ModelParameter as 'eurMbbl'] || 'normal';
    });

    form.setFieldValue(`${basePath}.initialGuess`, initialGuess);
    form.setFieldValue(`${basePath}.lowerBounds`, lowerBounds);
    form.setFieldValue(`${basePath}.upperBounds`, upperBounds);
    if (config.modelType === 'biased' && config.lossFunction === 'mlle') {
      form.setFieldValue(`${basePath}.distributions`, distributions);
    } else {
      form.deleteField(`${basePath}.distributions`);
    }
  };

  return (
    <>
      <div className={styles.formContainer}>
        {/* Model */}
        <form.Field name={`${basePath}.model`}>
          {(field) => (
            <TanstackFieldWrapper<Model> field={field}>
              {(props) => (
                <DropdownField
                  id={`${baseId}-model`}
                  label=""
                  titleText="Model"
                  items={
                    modelType === 'ramp_up'
                      ? (modelOptionsMapping.ramp_up as Model[])
                      : (modelOptionsMapping.non_ramp_up as Model[])
                  }
                  itemToString={(item: string | null) => startCase(item || '')}
                  {...props}
                  onChange={(value: Model) => {
                    props.onChange(value);

                    handleModelChange(value);
                  }}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>

        {/* Residual Function */}
        <form.Field name={`${basePath}.residualFunction`}>
          {(field) => (
            <TanstackFieldWrapper<string> field={field}>
              {(props) => (
                <DropdownField
                  id={`${baseId}-residual-function`}
                  label=""
                  titleText="Residual Function"
                  items={[...RESIDUAL_FUNCTION_OPTIONS]}
                  itemToString={(item: string | null) => startCase(item || '')}
                  {...props}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>

        {/* Loss Function */}
        <form.Field name={`${basePath}.lossFunction`}>
          {(field) => (
            <TanstackFieldWrapper<string> field={field}>
              {(props) => (
                <DropdownField
                  id={`${baseId}-loss-function`}
                  label=""
                  titleText="Loss Function"
                  items={
                    modelType === 'early_time'
                      ? ['mlle']
                      : modelType === 'biased'
                        ? [...LOSS_FUNCTION_OPTIONS, 'mlle']
                        : [...LOSS_FUNCTION_OPTIONS]
                  }
                  itemToString={(item: string | null) => startCase(item || '')}
                  {...props}
                  onChange={(value: string) => {
                    // console.log('value', value);
                    props.onChange(value);

                    const paramKeys = getMethodParameterMapping(
                      config.modelType
                    )[config.model];

                    if (value === 'mlle') {
                      const existingResidualSigma = getValueByPath(
                        baselines as unknown as Record<string, unknown>,
                        `${basePath}.residualSigma`
                      ) as number;
                      const existingProbabilityDistance = getValueByPath(
                        baselines as unknown as Record<string, unknown>,
                        `${basePath}.probabilityDistance`
                      ) as number;
                      form.setFieldValue(
                        `${basePath}.residualSigma`,
                        existingResidualSigma || 0.2
                      );
                      form.setFieldValue(
                        `${basePath}.probabilityDistance`,
                        existingProbabilityDistance || 0.9
                      );

                      const existingDistributions = getValueByPath(
                        baselines as unknown as Record<string, unknown>,
                        `${basePath}.distributions`
                      ) as Record<ModelParameter | 'eurMbbl', string>;
                      const distributionsData = {} as Record<
                        ModelParameter | 'eurMbbl',
                        string
                      >;
                      paramKeys.forEach((key) => {
                        distributionsData[key as ModelParameter] =
                          existingDistributions?.[key as ModelParameter] ||
                          'normal';
                      });

                      if (
                        config.modelType === 'biased' &&
                        config.lossFunction === 'mlle' &&
                        config.constrainEur
                      ) {
                        distributionsData.eurMbbl =
                          existingDistributions?.eurMbbl || 'normal';
                      }

                      form.setFieldValue(
                        `${basePath}.distributions`,
                        distributionsData
                      );
                      form.setFieldValue(`${basePath}.constrainEur`, false);
                    } else {
                      form.deleteField(`${basePath}.residualSigma`);
                      form.deleteField(`${basePath}.probabilityDistance`);
                      form.deleteField(`${basePath}.distributions`);
                      form.deleteField(`${basePath}.constrainEur`);
                    }
                  }}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>

        {/* Alpha */}
        <form.Field name={`${basePath}.alpha`}>
          {(field) => (
            <TanstackFieldWrapper<number | null> field={field}>
              {(props) => (
                <NumberField id={`${baseId}-alpha`} label="Alpha" {...props} />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>

        {/* QI Bound Type */}
        <form.Field name={`${basePath}.qiBoundType`}>
          {(field) => (
            <TanstackFieldWrapper<string> field={field}>
              {(props) => (
                <DropdownField
                  id={`${baseId}-qi-bound-type`}
                  label=""
                  titleText="qi bound type"
                  items={[...QI_BOUND_TYPE_OPTIONS]}
                  itemToString={(item: string | null) => startCase(item || '')}
                  {...props}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>

        {/* Max Months Online */}
        <form.Field name={`${basePath}.maxMonthsOnline`}>
          {(field) => (
            <TanstackFieldWrapper<number | null> field={field}>
              {(props) => (
                <NumberField
                  id={`${baseId}-max-months-online`}
                  label="Max Months Online"
                  {...props}
                />
              )}
            </TanstackFieldWrapper>
          )}
        </form.Field>

        {/* Conditional fields for Biased + MLLE */}
        {modelType === 'biased' && config.lossFunction === 'mlle' && (
          <>
            <form.Field name={`${basePath}.residualSigma`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`${baseId}-residual-sigma-biased`}
                      label="Residual Standard Deviation"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name={`${basePath}.probabilityDistance`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`${baseId}-probability-distance-biased`}
                      label="Probability Distance"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <div className={commonStyles.checkboxContainer}>
              <form.Field name={`${basePath}.constrainEur`}>
                {(field) => (
                  <TanstackFieldWrapper<boolean> field={field}>
                    {(props) => (
                      <Checkbox
                        id={`${baseId}-constrain-eur`}
                        labelText="Constrain EUR"
                        checked={props.value as boolean}
                        onChange={(_evt, { checked }: { checked: boolean }) => {
                          props.onChange(checked);
                          const existingInitialGuess = getValueByPath(
                            baselines as unknown as Record<string, unknown>,
                            `${basePath}.initialGuess`
                          ) as Record<ModelParameter | 'eurMbbl', number>;
                          const existingLowerBounds = getValueByPath(
                            baselines as unknown as Record<string, unknown>,
                            `${basePath}.lowerBounds`
                          ) as Record<ModelParameter | 'eurMbbl', number>;
                          const existingUpperBounds = getValueByPath(
                            baselines as unknown as Record<string, unknown>,
                            `${basePath}.upperBounds`
                          ) as Record<ModelParameter | 'eurMbbl', number>;
                          const existingDistributions = getValueByPath(
                            baselines as unknown as Record<string, unknown>,
                            `${basePath}.distributions`
                          ) as Record<ModelParameter | 'eurMbbl', string>;

                          if (checked) {
                            form.setFieldValue(
                              `${basePath}.initialGuess.eurMbbl`,
                              existingInitialGuess?.eurMbbl || 1000
                            );
                            form.setFieldValue(
                              `${basePath}.lowerBounds.eurMbbl`,
                              existingLowerBounds?.eurMbbl || 1000
                            );
                            form.setFieldValue(
                              `${basePath}.upperBounds.eurMbbl`,
                              existingUpperBounds?.eurMbbl || 1000
                            );
                            form.setFieldValue(
                              `${basePath}.distributions.eurMbbl`,
                              existingDistributions?.eurMbbl || 'normal'
                            );
                          } else {
                            form.deleteField(
                              `${basePath}.initialGuess.eurMbbl`
                            );
                            form.deleteField(`${basePath}.lowerBounds.eurMbbl`);
                            form.deleteField(`${basePath}.upperBounds.eurMbbl`);
                            form.deleteField(
                              `${basePath}.distributions.eurMbbl`
                            );
                          }
                        }}
                      />
                    )}
                  </TanstackFieldWrapper>
                )}
              </form.Field>
            </div>
          </>
        )}

        {/* Conditional fields for Early Time */}
        {modelType === 'early_time' && (
          <>
            <form.Field name={`${basePath}.residualSigma`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`${baseId}-residual-sigma-early-time`}
                      label="Residual Standard Deviation"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name={`${basePath}.eurToleranceFactor`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`${baseId}-eur-tolerance-factor`}
                      label="EUR Tolerance Factor"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name={`${basePath}.lateralLengthDegradationFactor`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`${baseId}-lateral-length-degradation-factor`}
                      label="Lateral Length Degradation Factor"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name={`${basePath}.lateralLengthDegradationFt`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`${baseId}-lateral-length-degradation-ft`}
                      label="Lateral Length Degradation Base ft"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name={`${basePath}.probabilityDistance`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`${baseId}-probability-distance-early-time`}
                      label="Probability Distance"
                      {...props}
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </>
        )}

        {/* Conditional fields for Ramp Up */}
        {modelType === 'ramp_up' && (
          <div className={commonStyles.checkboxContainer}>
            <form.Field name={`${basePath}.earlyTimeRampUpOnly`}>
              {(field) => (
                <TanstackFieldWrapper<boolean> field={field}>
                  {(props) => (
                    <Checkbox
                      id={`${baseId}-early-time-ramp-up-only`}
                      labelText="Early Time Ramp Up Only"
                      checked={props.value as boolean}
                      onChange={(_evt, { checked }: { checked: boolean }) =>
                        props.onChange(checked)
                      }
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>
        )}
      </div>
      {/* )}
    </form.Subscribe> */}
    </>
  );
};
